import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { reduxForm, getFormValues, Field, formValueSelector, submit } from "redux-form";

import { triggerSpreedlyValidation, getSpreedlyErrors } from "data/spreedly";
import { getRequireDonationAddress, getRequiredMinimumDonationAmount } from "data/entity";
import { getCountries } from "data/assets";
import { getUser } from "data/user";
import { getCampaign as getParentCampaign, getCustomFields } from "data/parent-campaign";

import {
  getValidating,
  setValidating,
  getSubmitError,
  submitVotingForm,
} from "data/voting-form/voting";

import { previousPage, nextPage } from "data/voting-form/routes";
import { logout } from "data/user";

import SpreedlyForm from "shared/components/spreedly-form";
import Address from "lib/components/Address";
import CustomFieldsComponent from "shared/components/custom-fields";
import InputField from "lib/components/InputField";
import FormattedNumberInput from "lib/components/FormattedNumberInput";
import DisplayError from "lib/components/DisplayError";

import donateFormValidation, {
  emailValidation,
  presenceValidation,
} from "lib/utils/donation-form-validation";
import { stripCommas } from "lib/utils/numberFunctions";

class Form extends React.Component {
  static propTypes = {
    handleSubmit: PropTypes.func.isRequired,
    submitting: PropTypes.bool.isRequired,
    backButton: PropTypes.func,
  };

  state = {
    loading: false,
  };

  constructor(props) {
    super(props);

    /*
      hack because redux forms ~7.3.0 doesn't handle validations properly
      when it gets passed a function. It re-renders when a new function is
      passed clearing the validation state. To get around this, access the data
      needed for validation on the class level instead of accessing it as a prop
    */
    this.minimumDonationAmount = props.minimumDonationAmount;
  }

  runValidation = (e) => {
    const { setValidating, triggerSpreedlyValidation } = this.props;

    window &&
      window.SnapAnalytics &&
      window.SnapAnalytics.track({
        hitType: "event",
        eventCategory: "giving form",
        eventAction: "donationFormButtonClicked",
      });

    e.preventDefault();
    triggerSpreedlyValidation(true);
    setValidating(true);
  };

  normalizeAmountValue = (value, previousValue, allValues, previousAllValues) => {
    let ret = stripCommas(value);

    if (isNaN(ret) || ret <= 0) {
      return 0.0;
    }
    return ret;
  };

  handleLogout = (e) => {
    const { logout } = this.props;

    e.preventDefault();

    this.setState({
      loading: true,
    });

    logout().then(() => {
      this.setState({
        loading: false,
      });
    });
  };

  handleBack = () => {
    const { previousPage } = this.props;

    previousPage();
  };

  afterSpreedlyValidation = () => {
    const { submit } = this.props;

    submit("campaign-donation");
  };

  afterFailedSpreedlyValidation = () => {
    const { setValidating } = this.props;

    setValidating(false);
  };

  isFormValid = (submitting, isValid) => {
    return !submitting && !isValid;
  };

  validateAmount = (value) => {
    const minAmount = this.minimumDonationAmount;
    const errorMessage = `Must be at least $${minAmount}.00`;

    if (typeof value === null || typeof value === undefined) {
      return errorMessage;
    }

    if (typeof value === "string") {
      const subbedValue = value.replace(/,/g, "");

      if (Number.isNaN(parseFloat(subbedValue))) {
        return errorMessage;
      }

      return parseFloat(subbedValue) < parseFloat(minAmount) ? errorMessage : undefined;
    }

    return parseFloat(value) < parseFloat(minAmount) ? errorMessage : undefined;
  };

  renderCurrentUser() {
    const { currentUser } = this.props;
    const { loading } = this.state;

    if (loading) {
      return (
        <div className="text-center">
          <i className="fa fa-2x fa-refresh fa-spin"></i>
        </div>
      );
    }

    if (currentUser && currentUser.get("id")) {
      return (
        <div className="p-sm-b p-xs-t text-center">
          You are giving and voting as{" "}
          {`${currentUser.get("first_name")} ${currentUser.get("last_name")}`}. &nbsp;Not{" "}
          {`${currentUser.get("first_name")}`}?
          <br />
          <a
            data-testid="t-voting-form--Form-a"
            className="pointer"
            onClick={this.handleLogout}>
            Click here.
          </a>
        </div>
      );
    } else {
      return (
        <div>
          <label className="required-field" htmlFor="donating_first_name">
            First Name
          </label>
          <Field
            name="first_name"
            component={InputField}
            validate={[presenceValidation]}
            type="text"
          />

          <label className="required-field" htmlFor="donating_last_name">
            Last Name
          </label>
          <Field
            name="last_name"
            component={InputField}
            validate={[presenceValidation]}
            type="text"
          />
          <label className="required-field" htmlFor="email">
            Email
          </label>
          <Field
            name="email"
            component={InputField}
            validate={[emailValidation]}
            type="text"
          />
        </div>
      );
    }
  }

  componentDidMount() {
    window &&
      window.SnapAnalytics &&
      window.SnapAnalytics.track({
        hitType: "event",
        eventCategory: "giving form",
        eventAction: "donationForm",
      });
  }

  renderButtonContent() {
    const { submitting } = this.props;
    const { validating } = this.state;

    if (submitting || validating) {
      return <i className="fa fa-lg fa-refresh fa-spin" />;
    }
    return "Give Now";
  }

  render() {
    const {
      submitting,
      submitFailed,
      submitError,
      handleSubmit,
      customFields,
      countries,
      requireAddress,
      first_name,
      last_name,
      month,
      year,
      address,
      city,
      region,
      zip,
      country,
      phone,
      parentCampaign,
      emptyMonthError,
      expiredYearError,
      valid,
      minimumDonationAmount,
    } = this.props;

    return (
      <form data-testid="t-voting-form--Form-form-2" onSubmit={handleSubmit}>
        <div className="voting-modal-body">
          <div className="donating-steps">
            <i
              className="donate-back-button primary-color-text pointer"
              onClick={this.handleBack}></i>
            <span
              className="step_credit primary-color-text secondary-color-border"
              style={{ borderBottomWidth: "2px", borderBottomStyle: "solid" }}>
              <i className="fa fa-credit-card-alt fa-lg m-sm-r"></i>
              Credit Card
            </span>
          </div>
          <div className="voting-modal-body-inside">
            <div className="donating-amount-area">
              <div className="donation-amount-section">
                <label htmlFor="donation_amount">Donation</label>

                <Field
                  inputClassName="donating_dollars_box"
                  name="amount"
                  inputIcon={<i className="fa fa-usd fa-lg primary-color-text" />}
                  component={InputField}
                  type="text"
                  isCurrency={true}
                  normalize={this.normalizeAmountValue}
                  validate={this.validateAmount}
                />
              </div>

              <div className="donation-help">
                <i className="fa fa-gift fa-3x primary-color-text"></i>
                <div className="text-left p-md-l">
                  <span>
                    Initial Gifts receive <strong>5 votes</strong>.
                  </span>
                  <br />
                  <span>
                    Additional gifts will receive <strong>1 vote</strong>.
                  </span>
                </div>
              </div>
              <br />
              <div className="donation-help">
                <span>
                  You are giving to <strong>{parentCampaign.get("name")}.</strong>
                </span>
              </div>
            </div>
            <div>
              <fieldset>
                {this.renderCurrentUser()}

                <SpreedlyForm
                  spreedlyKey={parentCampaign.get("spreedly_key")}
                  afterSpreedlyValidation={this.afterSpreedlyValidation}
                  afterFailedSpreedlyValidation={this.afterFailedSpreedlyValidation}
                  first_name={first_name}
                  last_name={last_name}
                  month={month}
                  year={year}
                  address={address}
                  city={city}
                  region={region}
                  zip={zip}
                  country={country}
                  phone={phone}
                />

                <label htmlFor="donating_exp" className="required-field label_two_thirds">
                  Expiration Date
                </label>
                <div className="row">
                  <div className="col-xs-12 col-md-12 m-sm-b">
                    <Field
                      name="card_exp"
                      placeholder="MM/YY"
                      fieldFormat="##/##"
                      fieldMask={["M", "M", "Y", "Y"]}
                      component={FormattedNumberInput}
                    />
                    <span className="text-danger">
                      {emptyMonthError}
                      {expiredYearError}
                    </span>
                  </div>
                </div>
                <div className="clearfix">
                  <Address
                    country={country}
                    showRequired={requireAddress}
                    countries={countries}
                  />
                </div>

                <div className="clearfix">
                  <CustomFieldsComponent
                    customFields={customFields}
                    wrapperClassName={"third"}
                    checkStepNumber={(number) => number <= 1}
                  />
                </div>
                <label>
                  <Field name="isPublic" component="input" type="checkbox" />
                </label>
                <span className="display">Display my name as a donor</span>
              </fieldset>
            </div>
          </div>
        </div>
        <div>
          <div className="text-center m-sm-t">
            {!submitting && submitFailed && !submitError && (
              <span className="white-font-color">
                Please check form above for missing required information
              </span>
            )}
            <DisplayError className="white-font-color" error={submitError} />
          </div>

          <button
            data-testid="t-voting-form--Form-submit"
            onClick={this.runValidation}
            className="voting-modal-btn secondary-color-background"
            disabled={!submitting && !valid}
            type="submit">
            {this.renderButtonContent()}
          </button>
        </div>
      </form>
    );
  }
}

Form = reduxForm({
  form: "campaign-donation",
  initialValues: {
    amount: 10.0,
    isPublic: true,
    year: "year",
    month: "month",
    card_exp: "",
    address: {
      country: "United States",
    },
  },
  validate: donateFormValidation,
})(Form);

const mapStateToProps = (state) => {
  const first_name = getUser(state)
    ? getUser(state).get("first_name")
    : formValueSelector("campaign-donation")(state, "first_name");

  const last_name = getUser(state)
    ? getUser(state).get("last_name")
    : formValueSelector("campaign-donation")(state, "last_name");

  let month, year;

  if (formValueSelector("campaign-donation")(state, "card_exp")) {
    [month, year] = formValueSelector("campaign-donation")(state, "card_exp").split("/");
  }

  return {
    minimumDonationAmount: getRequiredMinimumDonationAmount(state),
    submitError: getSubmitError(state),
    country: formValueSelector("campaign-donation")(state, "address.country"),
    address1: formValueSelector("campaign-donation")(state, "address.address1"),
    address2: formValueSelector("campaign-donation")(state, "address.address2"),
    city: formValueSelector("campaign-donation")(state, "address.city"),
    region: formValueSelector("campaign-donation")(state, "address.region"),
    zip: formValueSelector("campaign-donation")(state, "address.zip"),
    phone: formValueSelector("campaign-donation")(state, "address.cell_phone"),

    countries: getCountries(state),
    currentUser: getUser(state),
    customFields: getCustomFields(state)("voting_form"),
    parentCampaign: getParentCampaign(state)("voting_form"),
    requireAddress: getRequireDonationAddress(state),
    first_name,
    last_name,
    month,
    year,
    validating: getValidating(state),
    expiredYearError: getSpreedlyErrors(state).get("year"),
    emptyMonthError: getSpreedlyErrors(state).get("month"),
  };
};

const mapDispatchToProps = {
  logout,
  previousPage,
  nextPage,
  setValidating,
  submit,
  triggerSpreedlyValidation,
};

export default connect(mapStateToProps, mapDispatchToProps)(Form);
